import type { BarSeriesType, LineSeriesType } from '@mui/x-charts/models';
import { type FC, useCallback, useMemo, useState } from 'react';
import type { SalesTotal } from '../../data/sales-total';
import type { Store } from '../../data/store';
import { SegmentedToggle } from '../segmented-toggle';
import { thisYearColor, thisYearEstimatesColor } from './constants';
import { formatDisplayValue, getDisplayValue, getMarginPercent } from './helpers';
import {
  AverageOrderValueSummary,
  MarginSummary,
  SalesSummary,
  TransactionsSummary,
} from './summaries';
import type { DisplayType } from './types';
import { WidgetContainer } from './widget-container';
import {
  priorYearColor,
  seriesValues,
  YearOverYearChart,
  yearLineSeries,
} from './year-over-year-chart';

const createSalesData = (totals: SalesTotal[], displayType: DisplayType) => {
  return totals.map((total) => getDisplayValue(total, displayType));
};

type SalesDisplayProperties = {
  store: Store;
  displayType: DisplayType;
};

type MarginUnit = 'dollar' | 'percent';

export const StoreSalesStatsDisplay: FC<SalesDisplayProperties> = ({ store, displayType }) => {
  const [marginUnit, setMarginUnit] = useState<MarginUnit>('dollar');
  // Margin percentage only makes sense for the Gross Margin widget. In that mode
  // we drop the bars (you can't stack percentages) and plot lines only.
  const showPercent = displayType === 'Gross Margin' && marginUnit === 'percent';

  const { historicalYears, thisYearSales } = useMemo(() => {
    const historical = store.stats.sales.historical;
    const thisYearSales = historical.at(-1);
    if (!thisYearSales) {
      throw 'Missing this year sales data.';
    }

    // Get up to 4 previous years, excluding current year
    const historicalYears = historical.slice(-5, -1).reverse();

    return { historicalYears, thisYearSales };
  }, [store]);

  const valueFormatter = useCallback(
    (value: number | null) => formatDisplayValue(value, displayType, showPercent),
    [displayType, showPercent]
  );

  // Current-year series: a percentage line in percent mode, otherwise the
  // stacked actual + estimate bars.
  const currentYearSeries = useMemo(() => {
    if (showPercent) {
      return [
        yearLineSeries(
          thisYearSales.year,
          thisYearSales.monthly.map(getMarginPercent),
          thisYearColor,
          valueFormatter
        ),
      ] satisfies LineSeriesType[];
    }

    const thisYearData = createSalesData(thisYearSales.monthly, displayType);
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const salesYear = thisYearSales.year;

    const thisYearEstimatesData = createSalesData(
      store.stats.sales.current_year_estimates.monthly,
      displayType
    ).map((value, index) => {
      // Determine if this month is in the past
      // If sales year is less than current year, all months are in the past
      // If sales year equals current year, months before current month are in the past
      const isPastMonth =
        salesYear < currentYear || (salesYear === currentYear && index < currentMonth);

      if (isPastMonth) {
        return 0;
      }

      // For the current month, deduct the actual value from the estimates to make the bar stacking work correctly
      if (salesYear === currentYear && index === currentMonth) {
        if (displayType === 'Average Order Value') {
          // Can't stack averages, so suppress the estimate when actual data exists.
          // But when there's no actual data yet, show the full estimate to avoid an empty bar.
          return thisYearData[index] === 0 ? value : 0;
        }

        return Math.max(0, value - thisYearData[index]);
      }

      return value;
    });

    return [
      {
        type: 'bar' as const,
        label: `${thisYearSales?.year}`,
        data: thisYearData,
        id: 'thisYear',
        color: thisYearColor,
        valueFormatter,
        stack: 'currentYear',
      },
      {
        type: 'bar' as const,
        label: `${thisYearSales?.year} Est.`,
        data: thisYearEstimatesData,
        id: 'thisYearEstimates',
        color: thisYearEstimatesColor,
        valueFormatter: (value: number | null, { dataIndex }) => {
          // For the current month, re-add the current value to the estimates to make the tooltip content correct
          if (salesYear === currentYear && dataIndex === currentMonth) {
            if (displayType === 'Average Order Value') {
              // When showing full estimate (no actual data yet), display the estimate value.
              // When actual data exists, the estimate contribution is suppressed — show N/A.
              return thisYearData[dataIndex] === 0 ? valueFormatter(value) : 'N/A';
            }

            return valueFormatter((value || 0) + thisYearData[dataIndex]);
          }

          return valueFormatter(value);
        },
        stack: 'currentYear',
      },
    ] satisfies BarSeriesType[];
  }, [store, thisYearSales, displayType, valueFormatter, showPercent]);

  const historicalSeries = useMemo(
    () =>
      historicalYears.map((yearData, index) =>
        yearLineSeries(
          yearData.year,
          showPercent
            ? yearData.monthly.map(getMarginPercent)
            : createSalesData(yearData.monthly, displayType),
          priorYearColor(index),
          valueFormatter
        )
      ),
    [historicalYears, displayType, valueFormatter, showPercent]
  );

  const axisBounds = useMemo(() => {
    const values = seriesValues([...currentYearSeries, ...historicalSeries]);
    const max = values.length > 0 ? Math.max(...values) : 0;

    if (!showPercent) {
      // Dollars: anchor at 0, round the top up to a whole tick value as before.
      return { min: 0, max: Math.ceil(max * 1.1) };
    }

    // Margins cluster in a narrow band, so a 0-based axis flattens the variation.
    // Zoom to the data range with padding, snapped to whole percentage points.
    const min = values.length > 0 ? Math.min(...values) : 0;
    const pad = Math.max((max - min) * 0.25, 0.01);

    return {
      min: Math.max(0, Math.floor((min - pad) * 100) / 100),
      max: Math.ceil((max + pad) * 100) / 100,
    };
  }, [currentYearSeries, historicalSeries, showPercent]);

  return (
    <WidgetContainer
      title={displayType}
      action={
        displayType === 'Gross Margin' ? (
          <SegmentedToggle<MarginUnit>
            value={marginUnit}
            onChange={setMarginUnit}
            className="[&_button]:px-4 [&_button]:py-1 [&_button]:leading-5"
            options={[
              { value: 'dollar', label: '$' },
              { value: 'percent', label: '%' },
            ]}
          />
        ) : undefined
      }
    >
      {displayType === 'Gross Margin' && (
        <MarginSummary
          lastImported={store.stats.sales.last_transaction_date || store.updated_at}
          thisYearSales={thisYearSales}
          thisYearEstimates={store.stats.sales.current_year_estimates}
          historicalYears={historicalYears}
        />
      )}
      {displayType === 'Sales' && (
        <SalesSummary
          lastImported={store.stats.sales.last_transaction_date || store.updated_at}
          thisYearSales={thisYearSales}
          thisYearEstimates={store.stats.sales.current_year_estimates}
          historicalYears={historicalYears}
        />
      )}
      {displayType === 'Transactions' && (
        <TransactionsSummary
          lastImported={store.stats.sales.last_transaction_date || store.updated_at}
          thisYearSales={thisYearSales}
          thisYearEstimates={store.stats.sales.current_year_estimates}
          historicalYears={historicalYears}
        />
      )}
      {displayType === 'Average Order Value' && (
        <AverageOrderValueSummary
          lastImported={store.stats.sales.last_transaction_date || store.updated_at}
          thisYearSales={thisYearSales}
          thisYearEstimates={store.stats.sales.current_year_estimates}
          historicalYears={historicalYears}
        />
      )}
      <YearOverYearChart
        // A fixed plot: these cards fill their height with summary tiles, and
        // the chart under them is the same size on every one.
        height={300}
        series={[...currentYearSeries, ...historicalSeries]}
        yAxis={{
          ...axisBounds,
          valueFormatter: (value) => {
            if (value === null || value === 0) {
              return showPercent ? '0%' : '0';
            }

            return valueFormatter(value);
          },
        }}
      />
    </WidgetContainer>
  );
};
